/**
 * The staff's side of the Rule 502(b) package (S-30): an issuer's offering amount, bad-actor
 * inquiry and vault documents; the questions purchasers ask; the sophistication determinations.
 *
 * Publishing a document is the vault's own route (`offeringDocumentsService.publish`): it works
 * for any row of the vault, an issuer's included.
 */
import type { AxiosInstance } from 'axios';
import authService from './auth.service';
import type { Questionnaire } from './regD.service';

export type IssuerDocType = 'reg_d_package' | 'exhibit' | 'bad_actor_disclosure';

export interface Issuer {
  legal_entity_id: string;
  products: { product_id: string; name: string }[];
}

export interface OfferingAmount {
  amount_usd: string;
  tier: 'part_fs_b' | 'part_fs_c';
  basis: string;
  declared_at: string;
}

export interface CoveredPerson {
  name: string;
  role: string;
}

export interface Inquiry {
  inquiry_id: string;
  performed_at: string;
  result: 'none' | 'disclosed';
  covered_persons: CoveredPerson[];
  disclosure_document_id: string | null;
  notes: string | null;
}

export interface IssuerDocument {
  document_id: string;
  doc_type: IssuerDocType;
  exhibit_slot: string | null;
  version: number;
  file_hash: string;
  file_size: number;
  is_published: boolean;
  published_at: string | null;
  created_at: string;
  fs_basis: 'us_gaap' | 'ifrs_iasb' | null;
  fs_audited: boolean | null;
  fs_as_of: string | null;
  bad_actor_inquiry_id: string | null;
}

export interface IssuerDetail {
  legal_entity_id: string;
  offering_amounts: OfferingAmount[];
  inquiries: Inquiry[];
  documents: IssuerDocument[];
}

export interface PackageFactsBody {
  fs_basis: 'us_gaap' | 'ifrs_iasb';
  fs_audited: boolean;
  fs_as_of: string;
  bad_actor_inquiry_id: string;
}

export interface OpenQuestion {
  question_id: string;
  client_id: string;
  client_name: string;
  legal_entity_id: string;
  product_id: string;
  question: string;
  asked_at: string;
}

export interface PendingDetermination {
  answers_id: string;
  case_id: string;
  client_id: string;
  client_name: string;
  legal_entity_id: string;
  submitted_at: string;
}

export interface AnswersForReview {
  answers_id: string;
  case_id: string;
  questionnaire: Questionnaire;
  answers: Record<string, string>;
  submitted_at: string;
}

class RegDStaffService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async listIssuers(): Promise<Issuer[]> {
    const res = await this.api.get<{ items: Issuer[] }>('/api/v1/staff/reg-d/issuers');
    return res.data.items;
  }

  async getIssuer(entityId: string): Promise<IssuerDetail> {
    const res = await this.api.get<IssuerDetail>(`/api/v1/staff/reg-d/issuers/${entityId}`);
    return res.data;
  }

  async declareAmount(entityId: string, amountUsd: string, basis: string): Promise<void> {
    await this.api.post(`/api/v1/staff/reg-d/issuers/${entityId}/offering-amount`, {
      aggregate_price_usd: amountUsd,
      basis,
    });
  }

  async recordInquiry(
    entityId: string,
    body: {
      covered_persons: CoveredPerson[];
      result: 'none' | 'disclosed';
      disclosure_document_id: string | null;
      notes: string | null;
    },
  ): Promise<void> {
    await this.api.post(`/api/v1/staff/reg-d/issuers/${entityId}/inquiries`, body);
  }

  async presignDocument(
    entityId: string,
    docType: IssuerDocType,
    exhibitSlot: string | null,
  ): Promise<{ upload_url: string; object_key: string; version: number }> {
    const res = await this.api.post<{ upload_url: string; object_key: string; version: number }>(
      `/api/v1/staff/reg-d/issuers/${entityId}/documents/presign`,
      { doc_type: docType, exhibit_slot: exhibitSlot },
    );
    return res.data;
  }

  async confirmDocument(
    entityId: string,
    docType: IssuerDocType,
    exhibitSlot: string | null,
    objectKey: string,
    facts: PackageFactsBody | null,
  ): Promise<{ document_id: string; version: number }> {
    const res = await this.api.post<{ document_id: string; version: number }>(
      `/api/v1/staff/reg-d/issuers/${entityId}/documents/confirm`,
      { doc_type: docType, exhibit_slot: exhibitSlot, object_key: objectKey, ...facts },
    );
    return res.data;
  }

  async listQuestions(): Promise<OpenQuestion[]> {
    const res = await this.api.get<{ items: OpenQuestion[] }>('/api/v1/staff/reg-d/questions');
    return res.data.items;
  }

  async answerQuestion(questionId: string, answer: string): Promise<void> {
    await this.api.post(`/api/v1/staff/reg-d/questions/${questionId}/answer`, { answer });
  }

  async listDeterminations(): Promise<PendingDetermination[]> {
    const res = await this.api.get<{ items: PendingDetermination[] }>(
      '/api/v1/staff/reg-d/sophistication',
    );
    return res.data.items;
  }

  async readAnswers(answersId: string): Promise<AnswersForReview> {
    const res = await this.api.get<AnswersForReview>(
      `/api/v1/staff/reg-d/sophistication/${answersId}`,
    );
    return res.data;
  }

  async determine(
    answersId: string,
    outcome: 'qualified' | 'not_qualified',
    reason: string,
  ): Promise<void> {
    await this.api.post(`/api/v1/staff/reg-d/sophistication/${answersId}/determine`, {
      outcome,
      reason,
    });
  }
}

export default new RegDStaffService();
